import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Images,
  Plus,
  Trash2,
  Pencil,
  Star,
  FolderPlus,
  Upload,
  Loader2,
  X,
  Eye,
  GripVertical,
  ImageIcon,
  Filter,
  UploadCloud,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const GalleryManagement = ({ language = 'fr' }) => {
  // State
  const [photos, setPhotos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  // Photo dialog
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [editingPhoto, setEditingPhoto] = useState(null);
  const [photoTitle, setPhotoTitle] = useState('');
  const [photoDescription, setPhotoDescription] = useState('');
  const [photoCategoryId, setPhotoCategoryId] = useState('');
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoBase64, setPhotoBase64] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Category dialog
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryName, setCategoryName] = useState('');
  const [categoryDescription, setCategoryDescription] = useState('');
  
  // Preview dialog
  const [previewPhoto, setPreviewPhoto] = useState(null);

  // Batch upload state
  const [batchUploadOpen, setBatchUploadOpen] = useState(false);
  const [batchCategoryId, setBatchCategoryId] = useState('');
  const [batchFiles, setBatchFiles] = useState([]); // [{file, status, error}]
  const [batchUploading, setBatchUploading] = useState(false);

  const fileInputRef = useRef(null);
  const batchFileInputRef = useRef(null);

  // Fetch data
  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/gallery/categories`);
      setCategories(response.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, []);

  const fetchPhotos = useCallback(async () => {
    setLoading(true);
    try {
      const params = selectedCategory !== 'all' ? `?category_id=${selectedCategory}` : '';
      const response = await axios.get(`${API_URL}/gallery/photos${params}`);
      setPhotos(response.data || []);
    } catch (error) {
      console.error('Error fetching photos:', error);
      toast.error('Erreur lors du chargement des photos');
    } finally {
      setLoading(false);
    }
  }, [selectedCategory]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    fetchPhotos();
  }, [fetchPhotos]);

  // Handle image selection
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image trop volumineuse (max 5MB)');
      return;
    }

    // Create preview and convert to base64
    const reader = new FileReader();
    reader.onload = (event) => {
      setPhotoPreview(event.target.result);
      setPhotoBase64(event.target.result);
    };
    reader.readAsDataURL(file);
  };

  // Open photo dialog for new photo
  const openNewPhotoDialog = () => {
    setEditingPhoto(null);
    setPhotoTitle('');
    setPhotoDescription('');
    setPhotoCategoryId('');
    setPhotoPreview(null);
    setPhotoBase64('');
    setPhotoDialogOpen(true);
  };

  // Open photo dialog for editing
  const openEditPhotoDialog = (photo) => {
    setEditingPhoto(photo);
    setPhotoTitle(photo.title || '');
    setPhotoDescription(photo.description || '');
    setPhotoCategoryId(photo.category_id || '');
    setPhotoPreview(photo.image_url);
    setPhotoBase64(''); // Don't need base64 for edit (unless replacing image)
    setPhotoDialogOpen(true);
  };

  // Save photo
  const handleSavePhoto = async () => {
    if (!photoTitle.trim()) {
      toast.error('Le titre est obligatoire');
      return;
    }

    if (!editingPhoto && !photoBase64) {
      toast.error('Veuillez sélectionner une image');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('dermascan_token');
      
      if (editingPhoto) {
        // Update existing photo
        await axios.put(
          `${API_URL}/gallery/photos/${editingPhoto.id}`,
          {
            title: photoTitle,
            description: photoDescription,
            category_id: photoCategoryId || null
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Photo mise à jour');
      } else {
        // Create new photo
        await axios.post(
          `${API_URL}/gallery/photos`,
          {
            title: photoTitle,
            description: photoDescription,
            category_id: photoCategoryId || null,
            image_data: photoBase64
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Photo ajoutée');
      }
      
      setPhotoDialogOpen(false);
      fetchPhotos();
      fetchCategories(); // Update counts
    } catch (error) {
      console.error('Error saving photo:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  // Delete photo
  const handleDeletePhoto = async (photo) => {
    if (!confirm(`Supprimer la photo "${photo.title}" ?`)) return;

    try {
      const token = localStorage.getItem('dermascan_token');
      await axios.delete(
        `${API_URL}/gallery/photos/${photo.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Photo supprimée');
      fetchPhotos();
      fetchCategories();
    } catch (error) {
      console.error('Error deleting photo:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  // Toggle featured
  const handleToggleFeatured = async (photo) => {
    try {
      const token = localStorage.getItem('dermascan_token');
      await axios.post(
        `${API_URL}/gallery/photos/${photo.id}/toggle-featured`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(photo.is_featured ? 'Photo retirée de la une' : 'Photo mise en avant');
      fetchPhotos();
    } catch (error) {
      console.error('Error toggling featured:', error);
      toast.error('Erreur');
    }
  };

  // Category management
  const openNewCategoryDialog = () => {
    setEditingCategory(null);
    setCategoryName('');
    setCategoryDescription('');
    setCategoryDialogOpen(true);
  };

  const openEditCategoryDialog = (category) => {
    setEditingCategory(category);
    setCategoryName(category.name);
    setCategoryDescription(category.description || '');
    setCategoryDialogOpen(true);
  };

  const handleSaveCategory = async () => {
    if (!categoryName.trim()) {
      toast.error('Le nom est obligatoire');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('dermascan_token');
      
      if (editingCategory) {
        await axios.put(
          `${API_URL}/gallery/categories/${editingCategory.id}`,
          { name: categoryName, description: categoryDescription },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Catégorie mise à jour');
      } else {
        await axios.post(
          `${API_URL}/gallery/categories`,
          { name: categoryName, description: categoryDescription },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Catégorie créée');
      }
      
      setCategoryDialogOpen(false);
      fetchCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = async (category) => {
    if (!confirm(`Supprimer la catégorie "${category.name}" ? Les photos seront déplacées vers "Sans catégorie".`)) return;

    try {
      const token = localStorage.getItem('dermascan_token');
      await axios.delete(
        `${API_URL}/gallery/categories/${category.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Catégorie supprimée');
      if (selectedCategory === category.id) {
        setSelectedCategory('all');
      }
      fetchCategories();
      fetchPhotos();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  // ========== Batch upload ==========
  // Converts a File to a base64 data URL (Promise-based)
  const fileToDataURL = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const openBatchUpload = () => {
    setBatchCategoryId('');
    setBatchFiles([]);
    setBatchUploadOpen(true);
  };

  const handleBatchFilesPicked = (e) => {
    const picked = Array.from(e.target.files || []);
    // Filter to images only, enforce 5MB max per file
    const valid = [];
    const skipped = [];
    picked.forEach((f) => {
      if (!f.type.startsWith('image/')) {
        skipped.push(`${f.name} (pas une image)`);
      } else if (f.size > 5 * 1024 * 1024) {
        skipped.push(`${f.name} (> 5MB)`);
      } else {
        valid.push({ file: f, status: 'pending', error: null });
      }
    });
    if (skipped.length > 0) {
      toast.warning(`Ignoré : ${skipped.join(', ')}`);
    }
    setBatchFiles((prev) => [...prev, ...valid]);
    // Reset input so the same file can be re-selected if needed
    if (batchFileInputRef.current) batchFileInputRef.current.value = '';
  };

  const removeBatchFile = (idx) => {
    setBatchFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleBatchUpload = async () => {
    if (batchFiles.length === 0) {
      toast.error('Aucune photo à téléverser');
      return;
    }
    setBatchUploading(true);
    const token = localStorage.getItem('dermascan_token');
    let successCount = 0;
    let failCount = 0;

    // Upload sequentially to avoid hammering the API / hitting size limits
    for (let i = 0; i < batchFiles.length; i++) {
      const item = batchFiles[i];
      if (item.status === 'done') continue;
      // Mark as uploading
      setBatchFiles((prev) => prev.map((b, idx) => idx === i ? { ...b, status: 'uploading' } : b));
      try {
        const dataUrl = await fileToDataURL(item.file);
        // Use filename (without extension) as default title for easy later identification
        const defaultTitle = item.file.name.replace(/\.[^/.]+$/, '');
        await axios.post(
          `${API_URL}/gallery/photos`,
          {
            title: defaultTitle,
            description: null,
            category_id: batchCategoryId || null,
            image_data: dataUrl,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setBatchFiles((prev) => prev.map((b, idx) => idx === i ? { ...b, status: 'done' } : b));
        successCount++;
      } catch (error) {
        const msg = error.response?.data?.detail || error.message || 'Erreur';
        setBatchFiles((prev) => prev.map((b, idx) => idx === i ? { ...b, status: 'error', error: msg } : b));
        failCount++;
      }
    }

    setBatchUploading(false);
    if (successCount > 0) {
      toast.success(`${successCount} photo${successCount > 1 ? 's' : ''} téléversée${successCount > 1 ? 's' : ''}${failCount > 0 ? ` — ${failCount} en échec` : ''}`);
      fetchPhotos();
      fetchCategories();
    }
    if (failCount > 0 && successCount === 0) {
      toast.error(`Échec du téléversement de ${failCount} photo${failCount > 1 ? 's' : ''}`);
    }
  };

  const closeBatchUpload = () => {
    if (batchUploading) return;
    setBatchUploadOpen(false);
    setBatchFiles([]);
    setBatchCategoryId('');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Images className="w-5 h-5 text-emerald-600" />
              Photothèque
            </CardTitle>
            <div className="flex gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={openNewCategoryDialog}
              >
                <FolderPlus className="w-4 h-4 mr-2" />
                Nouvelle catégorie
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={openBatchUpload}
                data-testid="open-batch-upload"
              >
                <UploadCloud className="w-4 h-4 mr-2" />
                Importer plusieurs
              </Button>
              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={openNewPhotoDialog}
              >
                <Plus className="w-4 h-4 mr-2" />
                Ajouter une photo
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Categories management */}
          <div className="mb-6">
            <Label className="text-sm font-medium text-gray-700 mb-2 block">Catégories</Label>
            <div className="flex flex-wrap gap-2">
              <Badge
                variant={selectedCategory === 'all' ? 'default' : 'outline'}
                className="cursor-pointer px-3 py-1"
                onClick={() => setSelectedCategory('all')}
              >
                Toutes ({photos.length})
              </Badge>
              {categories.map((cat) => (
                <div key={cat.id} className="flex items-center gap-1 group">
                  <Badge
                    variant={selectedCategory === cat.id ? 'default' : 'outline'}
                    className="cursor-pointer px-3 py-1"
                    onClick={() => setSelectedCategory(cat.id)}
                  >
                    {cat.name} ({cat.photo_count || 0})
                  </Badge>
                  <button
                    onClick={() => openEditCategoryDialog(cat)}
                    className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-100 rounded transition-opacity"
                  >
                    <Pencil className="w-3 h-3 text-gray-500" />
                  </button>
                  <button
                    onClick={() => handleDeleteCategory(cat)}
                    className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-100 rounded transition-opacity"
                  >
                    <Trash2 className="w-3 h-3 text-red-500" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Photos grid */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
            </div>
          ) : photos.length === 0 ? (
            <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-xl">
              <ImageIcon className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">Aucune photo dans cette catégorie</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={openNewPhotoDialog}
              >
                <Plus className="w-4 h-4 mr-2" />
                Ajouter une photo
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {photos.map((photo) => (
                <div
                  key={photo.id}
                  className="group relative bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                >
                  {/* Image */}
                  <div 
                    className="aspect-square bg-gray-100 cursor-pointer"
                    onClick={() => setPreviewPhoto(photo)}
                  >
                    <img
                      src={photo.image_url}
                      alt={photo.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>

                  {/* Featured badge */}
                  {photo.is_featured && (
                    <div className="absolute top-2 left-2">
                      <Badge className="bg-amber-500 text-white">
                        <Star className="w-3 h-3 mr-1 fill-white" />
                        En vedette
                      </Badge>
                    </div>
                  )}

                  {/* Actions overlay */}
                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => handleToggleFeatured(photo)}
                      className={`p-1.5 rounded-full ${photo.is_featured ? 'bg-amber-500 text-white' : 'bg-white text-gray-600'} shadow hover:scale-110 transition-transform`}
                      title={photo.is_featured ? 'Retirer de la une' : 'Mettre en vedette'}
                    >
                      <Star className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => openEditPhotoDialog(photo)}
                      className="p-1.5 rounded-full bg-white text-gray-600 shadow hover:scale-110 transition-transform"
                      title="Modifier"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeletePhoto(photo)}
                      className="p-1.5 rounded-full bg-white text-red-500 shadow hover:scale-110 transition-transform"
                      title="Supprimer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Info */}
                  <div className="p-3">
                    <h4 className="font-medium text-sm text-gray-900 truncate">{photo.title}</h4>
                    <p className="text-xs text-gray-500 truncate">{photo.category_name}</p>
                    {photo.views > 0 && (
                      <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                        <Eye className="w-3 h-3" />
                        {photo.views} vues
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Photo Dialog */}
      <Dialog open={photoDialogOpen} onOpenChange={setPhotoDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] flex flex-col gap-0 p-0">
          <DialogHeader className="shrink-0 px-6 pt-6 pb-3 border-b border-zinc-200">
            <DialogTitle className="flex items-center gap-2">
              {editingPhoto ? <Pencil className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
              {editingPhoto ? 'Modifier la photo' : 'Ajouter une photo'}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto min-h-0 px-6 py-4 space-y-4">
            {/* Image upload/preview */}
            <div>
              <Label>Image</Label>
              <div 
                className="mt-2 border-2 border-dashed border-gray-300 rounded-xl p-4 text-center cursor-pointer hover:border-emerald-400 transition-colors"
                onClick={() => !editingPhoto && fileInputRef.current?.click()}
              >
                {photoPreview ? (
                  <div className="relative">
                    <img
                      src={photoPreview}
                      alt="Preview"
                      className="max-h-48 mx-auto rounded-lg object-contain"
                    />
                    {!editingPhoto && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setPhotoPreview(null);
                          setPhotoBase64('');
                        }}
                        className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="py-8">
                    <Upload className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                    <p className="text-sm text-gray-500">
                      Cliquez pour sélectionner une image
                    </p>
                    <p className="text-xs text-gray-400 mt-1">PNG, JPG jusqu'à 5MB</p>
                  </div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                className="hidden"
              />
            </div>

            {/* Title */}
            <div>
              <Label htmlFor="photo-title">Titre *</Label>
              <Input
                id="photo-title"
                value={photoTitle}
                onChange={(e) => setPhotoTitle(e.target.value)}
                placeholder="Titre de la photo"
              />
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="photo-desc">Description</Label>
              <Textarea
                id="photo-desc"
                value={photoDescription}
                onChange={(e) => setPhotoDescription(e.target.value)}
                placeholder="Description optionnelle..."
                rows={2}
              />
            </div>

            {/* Category */}
            <div>
              <Label>Catégorie</Label>
              <Select value={photoCategoryId || "none"} onValueChange={(val) => setPhotoCategoryId(val === "none" ? "" : val)}>
                <SelectTrigger>
                  <SelectValue placeholder="Sans catégorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sans catégorie</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter className="shrink-0 px-6 py-3 border-t border-zinc-200">
            <Button variant="outline" onClick={() => setPhotoDialogOpen(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleSavePhoto} 
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingPhoto ? 'Enregistrer' : 'Ajouter'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category Dialog */}
      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderPlus className="w-5 h-5" />
              {editingCategory ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="cat-name">Nom *</Label>
              <Input
                id="cat-name"
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
                placeholder="Ex: Équipe, Événements, Produits..."
              />
            </div>
            <div>
              <Label htmlFor="cat-desc">Description</Label>
              <Textarea
                id="cat-desc"
                value={categoryDescription}
                onChange={(e) => setCategoryDescription(e.target.value)}
                placeholder="Description optionnelle..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCategoryDialogOpen(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleSaveCategory} 
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingCategory ? 'Enregistrer' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog (Lightbox) */}
      <Dialog open={!!previewPhoto} onOpenChange={() => setPreviewPhoto(null)}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden bg-black/95">
          {previewPhoto && (
            <div className="relative">
              <button
                onClick={() => setPreviewPhoto(null)}
                className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white z-10 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
              <img
                src={previewPhoto.image_url}
                alt={previewPhoto.title}
                className="w-full max-h-[80vh] object-contain"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 text-white">
                <h3 className="text-xl font-semibold">{previewPhoto.title}</h3>
                {previewPhoto.description && (
                  <p className="text-white/80 mt-1">{previewPhoto.description}</p>
                )}
                <p className="text-white/60 text-sm mt-2">{previewPhoto.category_name}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Batch Upload Dialog */}
      <Dialog open={batchUploadOpen} onOpenChange={(open) => !open && closeBatchUpload()}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col gap-0 p-0">
          <DialogHeader className="shrink-0 px-6 pt-6 pb-3 border-b border-zinc-200">
            <DialogTitle className="flex items-center gap-2">
              <UploadCloud className="w-5 h-5 text-emerald-600" />
              Importer plusieurs photos
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto min-h-0 px-6 py-4 space-y-4">
            <p className="text-sm text-gray-600">
              Sélectionnez plusieurs images d'un coup. Elles seront téléversées avec le nom du fichier
              comme titre provisoire. Vous pourrez ensuite cliquer sur chaque photo pour modifier le titre,
              la description et la catégorie.
            </p>

            {/* Optional batch category picker */}
            <div>
              <Label>Catégorie (optionnel — appliquée à toutes)</Label>
              <Select
                value={batchCategoryId || 'none'}
                onValueChange={(val) => setBatchCategoryId(val === 'none' ? '' : val)}
              >
                <SelectTrigger data-testid="batch-category-select">
                  <SelectValue placeholder="Sans catégorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sans catégorie</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* File picker zone */}
            <div
              className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center cursor-pointer hover:border-emerald-400 transition-colors"
              onClick={() => !batchUploading && batchFileInputRef.current?.click()}
              data-testid="batch-file-drop"
            >
              <UploadCloud className="w-10 h-10 mx-auto text-gray-400 mb-2" />
              <p className="text-sm text-gray-600 font-medium">
                Cliquez pour sélectionner plusieurs images
              </p>
              <p className="text-xs text-gray-400 mt-1">PNG, JPG — max 5 Mo par image</p>
              <input
                ref={batchFileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleBatchFilesPicked}
                className="hidden"
                data-testid="batch-file-input"
              />
            </div>

            {/* Files queue */}
            {batchFiles.length > 0 && (
              <div className="border border-gray-200 rounded-lg divide-y max-h-64 overflow-y-auto">
                {batchFiles.map((b, idx) => (
                  <div key={idx} className="flex items-center gap-3 px-3 py-2 text-sm">
                    <div className="shrink-0">
                      {b.status === 'done' && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
                      {b.status === 'error' && <AlertCircle className="w-4 h-4 text-red-500" />}
                      {b.status === 'uploading' && <Loader2 className="w-4 h-4 animate-spin text-emerald-500" />}
                      {b.status === 'pending' && <ImageIcon className="w-4 h-4 text-gray-400" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="truncate font-medium text-gray-800">{b.file.name}</p>
                      <p className="text-xs text-gray-500">
                        {(b.file.size / 1024).toFixed(0)} Ko
                        {b.status === 'error' && b.error && (
                          <span className="text-red-500 ml-2">• {b.error}</span>
                        )}
                        {b.status === 'done' && <span className="text-emerald-600 ml-2">• Téléversée</span>}
                      </p>
                    </div>
                    {!batchUploading && b.status !== 'done' && (
                      <button
                        onClick={() => removeBatchFile(idx)}
                        className="p-1 text-gray-400 hover:text-red-500"
                        aria-label="Retirer"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {batchFiles.length > 0 && (
              <p className="text-xs text-gray-500">
                {batchFiles.filter(b => b.status === 'done').length} / {batchFiles.length} téléversée
                {batchFiles.length > 1 ? 's' : ''}
              </p>
            )}
          </div>

          <DialogFooter className="shrink-0 px-6 py-3 border-t border-zinc-200">
            <Button
              variant="outline"
              onClick={closeBatchUpload}
              disabled={batchUploading}
            >
              Fermer
            </Button>
            <Button
              onClick={handleBatchUpload}
              disabled={batchUploading || batchFiles.length === 0 || batchFiles.every(b => b.status === 'done')}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="batch-upload-submit"
            >
              {batchUploading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {batchUploading
                ? 'Téléversement en cours...'
                : `Téléverser ${batchFiles.filter(b => b.status !== 'done').length} photo${batchFiles.filter(b => b.status !== 'done').length > 1 ? 's' : ''}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GalleryManagement;
